/**
 * FieldModeReviewModal - Saha modu talebini onaylama/reddetme modalı
 * (OvertimeApprovalModal.tsx + OvertimeRejectModal.tsx desenlerinin tek modalda birleşimi)
 */

import React, { useState, useEffect } from 'react';
import { Modal, Input, Form } from 'antd';
import { FieldModeRequestDto } from '../../../../types/fieldModeRequest.types';

const { TextArea } = Input;

interface FieldModeReviewModalProps {
  visible: boolean;
  action: 'approve' | 'reject' | null;
  request: FieldModeRequestDto | null;
  onConfirm: (requestId: number, action: 'approve' | 'reject', rejectionReason?: string) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const FieldModeReviewModal: React.FC<FieldModeReviewModalProps> = ({
  visible,
  action,
  request,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setRejectionReason('');
    }
  }, [visible, form]);

  if (!request || !action) {
    return null;
  }

  const modeLabel = request.requestedMode === 'field' ? 'Sahaya Çıkış' : 'Ofise Dönüş';
  const isApprove = action === 'approve';

  const handleConfirm = async () => {
    if (isApprove) {
      await onConfirm(request.id, 'approve');
      return;
    }

    try {
      await form.validateFields();
      const trimmedReason = rejectionReason.trim();
      await onConfirm(request.id, 'reject', trimmedReason || undefined);
    } catch {
      // form validasyon hatası - işlem yapma
    }
  };

  return (
    <Modal
      title={
        <div className="d-flex align-items-center gap-2">
          <i className={`ti ${isApprove ? 'ti-check text-success' : 'ti-x text-danger'}`} style={{ fontSize: '1.5rem' }} />
          <span>{isApprove ? 'Talebi Onayla' : 'Talebi Reddet'}</span>
        </div>
      }
      open={visible}
      onOk={handleConfirm}
      onCancel={onCancel}
      confirmLoading={loading}
      okText={isApprove ? 'Evet, Onayla' : 'Reddet'}
      cancelText="İptal"
      okButtonProps={{ danger: !isApprove }}
      cancelButtonProps={{ disabled: loading }}
      width={520}
      centered
    >
      <div className="mb-3 pb-3 border-bottom">
        <div className="row">
          <div className="col-6 mb-2">
            <span className="text-muted d-block" style={{ fontSize: '0.875rem' }}>Çalışan</span>
            <strong>{request.user.firstName} {request.user.lastName}</strong>
          </div>
          <div className="col-6 mb-2">
            <span className="text-muted d-block" style={{ fontSize: '0.875rem' }}>Talep</span>
            <strong>{modeLabel}</strong>
          </div>
        </div>
      </div>

      {isApprove ? (
        <p className="mb-0 fw-medium text-center">
          Bu talebi onaylarsanız, çalışanın modu hemen "{modeLabel}" olarak güncellenecek.
        </p>
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item
            label={<span className="fw-medium">Red Nedeni</span>}
            name="rejectionReason"
            rules={[
              { max: 500, message: 'Red nedeni en fazla 500 karakter olabilir' },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Red nedeni (opsiyonel, çalışana iletilir)"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              maxLength={500}
              showCount
              disabled={loading}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default FieldModeReviewModal;
